import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
    Link,
    NavLink,
    Outlet,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { navigateToTop } from "../../utils/Constants";
import useApiProfile from "../../hooks/useApiProfile";
import { API_BASE } from "../../config/apiBase";
import DashboardTour from "../../component/tour/DashboardTour";
import { profileNeedsDashboardTour } from "../../utils/onboarding";
import "../../styles/pages/interview/_dashboard.scss";

// ─── Sidebar width tokens (kept in sync with SCSS CSS vars) ──────────────────
const SIDEBAR_OPEN = 264;
const SIDEBAR_CLOSED = 68;
const MOBILE_BP = 768;

const STORAGE_KEY = "dashboard.sidebar.minimised";
const DASHBOARD_TOUR_PENDING_KEY = "mooz:dashboardTourPending";
const dashboardTourSeenKey = (userId) => `mooz:dashboardTourSeen:v1:${userId}`;

function readStoredMinimised() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v === null ? null : v === "true";
    } catch {
        return null;
    }
}

export default function Dashboard() {
    const stored = readStoredMinimised();
    const [minimised, setMinimised] = useState(stored ?? false);
    const hasStored = useRef(stored !== null);
    const skipPersist = useRef(true);
    const suppressPersist = useRef(false);
    const hoverAutoExpanded = useRef(false);
    const hoverExpandTimer = useRef(null);
    const minimisedRef = useRef(minimised);
    const tourOpenRef = useRef(false);
    const hoverExpandEnabledRef = useRef(false);
    const dashboardRef = useRef(null);
    const mainRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, getAccessTokenSilently } = useAuth0();
    const { profile } = useApiProfile();
    const [tourOpen, setTourOpen] = useState(false);
    const [hoverExpandEnabled, setHoverExpandEnabled] = useState(false);
    const forceTour = useMemo(
        () => new URLSearchParams(location.search).get("tour") === "1",
        [location.search]
    );

    const displayName = useMemo(() => {
        const dbName =
            `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim();
        if (dbName) return dbName;
        if (!user) return "Moozer";
        return (
            `${user.given_name ?? ""} ${user.family_name ?? ""}`.trim() ||
            user.name ||
            "Moozer"
        );
    }, [profile?.first_name, profile?.last_name, user]);

    const initials = displayName
        ? displayName
              .split(" ")
              .map((w) => w[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()
        : "M";

    const tabs = useMemo(
        () => [
            { link: "/dashboard", label: "Home", icon: "home", end: true },
            {
                link: "/dashboard/questions",
                label: "Question bank",
                icon: "question",
            },
            {
                link: "/dashboard/recordings",
                label: "Recordings",
                icon: "recording",
            },
        ],
        []
    );

    // ── Auto-collapse on mobile ──────────────────────────────────────────────
    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${MOBILE_BP}px)`);
        const sync = () => {
            if (!hasStored.current) setMinimised(mq.matches);
        };
        sync();
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);

    // ── Persist preference ───────────────────────────────────────────────────
    useEffect(() => {
        if (skipPersist.current) {
            skipPersist.current = false;
            return;
        }
        if (suppressPersist.current) {
            suppressPersist.current = false;
            return;
        }
        hasStored.current = true;
        try {
            localStorage.setItem(STORAGE_KEY, String(minimised));
        } catch {
            /* noop */
        }
    }, [minimised]);

    // ── Desktop hover auto-expand ────────────────────────────────────────────
    useEffect(() => {
        const mq = window.matchMedia(
            "(hover: hover) and (pointer: fine) and (min-width: 1024px)"
        );
        const sync = () => setHoverExpandEnabled(mq.matches);
        sync();
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);

    // ── Mobile horizontal scroll sync ───────────────────────────────────────
    useEffect(() => {
        const el = dashboardRef.current;
        if (!el || !window.matchMedia(`(max-width: ${MOBILE_BP}px)`).matches)
            return;
        const target = minimised ? el.clientWidth : 0;
        requestAnimationFrame(() =>
            el.scrollTo({ left: target, behavior: "smooth" })
        );
    }, [minimised]);

    // ── Scroll to top on route change ───────────────────────────────────────
    useEffect(() => {
        mainRef.current?.scrollTo({ top: 0, behavior: "instant" });
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [location.pathname]);

    useEffect(() => {
        minimisedRef.current = minimised;
    }, [minimised]);

    useEffect(() => {
        tourOpenRef.current = tourOpen;
    }, [tourOpen]);

    useEffect(() => {
        hoverExpandEnabledRef.current = hoverExpandEnabled;
    }, [hoverExpandEnabled]);

    // ── Close sidebar overlay on mobile when navigating ─────────────────────
    useEffect(() => {
        if (window.matchMedia(`(max-width: ${MOBILE_BP}px)`).matches) {
            setMinimised(true);
        }
    }, [location.pathname]);

    // ── Tour (first login) ────────────────────────────────────────────────
    useEffect(() => {
        if (!location.pathname.startsWith("/dashboard")) return;

        if (forceTour) {
            setTourOpen(true);
            return;
        }

        const id = profile?.id;
        if (!id) return;

        let pending = false;
        let seen = false;
        try {
            pending = localStorage.getItem(DASHBOARD_TOUR_PENDING_KEY) === "1";
            seen = localStorage.getItem(dashboardTourSeenKey(id)) === "1";
        } catch {
            pending = false;
            seen = false;
        }

        const needsTour = profileNeedsDashboardTour(profile);
        if ((pending || needsTour) && !seen) {
            setTourOpen(true);
        }
    }, [forceTour, location.pathname, profile, profile?.id]);

    const saveRole = useCallback(
        async ({ role, firstName, lastName } = {}) => {
            const next = {};
            if (role) next.role = role;
            if (typeof firstName === "string") {
                const v = firstName.trim();
                if (v) next.first_name = v;
            }
            if (typeof lastName === "string") {
                const v = lastName.trim();
                if (v) next.last_name = v;
            }
            if (!Object.keys(next).length) return true;

            const apiPayload = {
                ...(next.role != null ? { role: next.role } : null),
                ...(next.first_name != null
                    ? { firstName: next.first_name }
                    : null),
                ...(next.last_name != null
                    ? { lastName: next.last_name }
                    : null),
            };

            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                },
            });
            const res = await fetch(`${API_BASE}/profile`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(apiPayload),
            });
            if (!res.ok) return false;
            let data = null;
            try {
                data = await res.json();
            } catch {
                data = null;
            }
            const detail =
                data && typeof data === "object"
                    ? data.profile || data.user || data
                    : null;
            if (typeof window !== "undefined") {
                window.dispatchEvent(
                    new CustomEvent("mooz:profileUpdated", {
                        detail:
                            detail && typeof detail === "object"
                                ? detail
                                : next,
                    })
                );
            }
            return true;
        },
        [getAccessTokenSilently]
    );

    const finishTour = useCallback(
        async ({ role } = {}) => {
            try {
                await saveRole({ role });
            } catch {
                /* noop */
            }
            try {
                localStorage.removeItem(DASHBOARD_TOUR_PENDING_KEY);
                if (profile?.id) {
                    localStorage.setItem(dashboardTourSeenKey(profile.id), "1");
                }
            } catch {
                /* noop */
            }
            setTourOpen(false);
            if (forceTour) {
                navigate(location.pathname, { replace: true });
            }
        },
        [forceTour, location.pathname, navigate, profile?.id, saveRole]
    );

    const toggle = () => {
        hoverAutoExpanded.current = false;
        setMinimised((v) => !v);
    };

    const cancelHoverExpand = useCallback(() => {
        if (hoverExpandTimer.current) {
            clearTimeout(hoverExpandTimer.current);
            hoverExpandTimer.current = null;
        }
    }, []);

    const scheduleHoverExpand = useCallback(() => {
        if (!hoverExpandEnabledRef.current) return;
        if (tourOpenRef.current) return;
        if (!minimisedRef.current) return;

        cancelHoverExpand();
        hoverExpandTimer.current = setTimeout(() => {
            hoverExpandTimer.current = null;
            if (!hoverExpandEnabledRef.current) return;
            if (tourOpenRef.current) return;
            if (!minimisedRef.current) return;

            hoverAutoExpanded.current = true;
            suppressPersist.current = true;
            setMinimised(false);
        }, 680);
    }, [cancelHoverExpand]);

    useEffect(() => cancelHoverExpand, [cancelHoverExpand]);

    const handleSidebarMouseLeave = () => {
        cancelHoverExpand();
        if (!hoverExpandEnabledRef.current) return;
        if (tourOpenRef.current) return;
        if (!hoverAutoExpanded.current) return;
        hoverAutoExpanded.current = false;
        suppressPersist.current = true;
        setMinimised(true);
    };

    return (
        <div
            ref={dashboardRef}
            className={`dashboard${minimised ? " is-minimised" : ""}`}
            data-sidebar={minimised ? "closed" : "open"}
        >
            {/* ── Sidebar ── */}
            <aside
                className="sidebar"
                aria-label="Dashboard navigation"
                onMouseLeave={handleSidebarMouseLeave}
            >
                {/* Top section */}
                <div className="sidebar__top">
                    {/* Logo / wordmark */}
                    <Link
                        to="/"
                        className="sidebar__brand"
                        aria-label="Back to Mooz home"
                        onClick={navigateToTop}
                        onMouseEnter={scheduleHoverExpand}
                        onMouseLeave={cancelHoverExpand}
                    >
                        <span
                            className="sidebar__brand-icon"
                            aria-hidden="true"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                stroke="none"
                            >
                                <path d="M20.84 12c0 2.05 .985 3.225 -.04 5c-1.026 1.775 -2.537 1.51 -4.314 2.534c-1.776 1.026 -2.302 2.466 -4.353 2.466c-2.051 0 -2.576 -1.441 -4.353 -2.466c-1.776 -1.024 -3.288 -.759 -4.314 -2.534c-1.025 -1.775 -.04 -2.95 -.04 -5s-.985 -3.225 .04 -5c1.026 -1.775 2.537 -1.51 4.314 -2.534c1.776 -1.026 2.302 -2.466 4.353 -2.466s2.577 1.441 4.353 2.466c1.776 1.024 3.288 .759 4.313 2.534c1.026 1.775 .04 2.95 .04 5z" />
                            </svg>
                        </span>
                        <span className="sidebar__brand-name">Mooz</span>
                    </Link>

                    {/* Nav items */}
                    <nav className="sidebar__nav" aria-label="Main sections">
                        <ul role="list" className="sidebar__nav-list">
                            {tabs.map((tab) => (
                                <li key={tab.link}>
                                    <NavLink
                                        to={tab.link}
                                        end={tab.end}
                                        className={({ isActive }) =>
                                            `sidebar__nav-item${isActive ? " is-active" : ""}`
                                        }
                                        onMouseEnter={scheduleHoverExpand}
                                        onMouseLeave={cancelHoverExpand}
                                    >
                                        <span
                                            className={`sidebar__nav-icon sidebar__nav-icon--${tab.icon}`}
                                            aria-hidden="true"
                                        />
                                        <span className="sidebar__nav-label">
                                            {tab.label}
                                        </span>

                                        {/* Active pip */}
                                        <span
                                            className="sidebar__nav-pip"
                                            aria-hidden="true"
                                        />
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>

                {/* Bottom section */}
                <div className="sidebar__bottom">
                    {/* Profile link */}
                    <Link
                        to="/profile"
                        className="sidebar__profile"
                        onMouseEnter={scheduleHoverExpand}
                        onMouseLeave={cancelHoverExpand}
                    >
                        <span className="sidebar__avatar" aria-hidden="true">
                            {initials}
                        </span>
                        <span className="sidebar__profile-name">
                            {displayName}
                        </span>
                    </Link>

                    {/* Collapse toggle */}
                    <button
                        type="button"
                        className="sidebar__toggle"
                        onClick={toggle}
                        aria-expanded={!minimised}
                        aria-label={
                            minimised ? "Expand sidebar" : "Collapse sidebar"
                        }
                        title={
                            minimised ? "Expand sidebar" : "Collapse sidebar"
                        }
                    >
                        <svg
                            className="sidebar__toggle-icon"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M7 6v12" />
                            <path d="M18 6l-6 6l6 6" />
                        </svg>
                    </button>
                </div>
            </aside>

            {/* ── Mobile overlay backdrop ── */}
            {!minimised && (
                <div
                    className="sidebar__backdrop"
                    aria-hidden="true"
                    onClick={() => setMinimised(true)}
                />
            )}

            {/* ── Main content ── */}
            <main
                className="dashboard__main"
                ref={mainRef}
                aria-live="polite"
                id="main-content"
            >
                <Outlet />
            </main>

            <DashboardTour
                open={tourOpen}
                onFinish={finishTour}
                onSaveProfile={saveRole}
                initialFirstName={profile?.first_name || ""}
                initialLastName={profile?.last_name || ""}
            />
        </div>
    );
}
